import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import authService from '../services/authService';
import './ResetPassword.css';

function ResetPassword() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { isDarkMode } = useTheme();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error("Please enter a valid email address.");
      }

      await authService.forgotPassword(email);
      setMessage("If an account with that email exists, you will receive a password reset link.");
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.message || "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Theme-specific styles
  const themeStyles = useMemo(() => {
    return {
      container: {
        background: isDarkMode 
          ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
          : '#eceff4'
      },
      content: {
        background: isDarkMode 
          ? 'linear-gradient(145deg, #151e30 0%, #121a2c 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f5f9ff 100%)',
        boxShadow: isDarkMode 
          ? '0 6px 12px rgba(0, 0, 0, 0.3)' 
          : '0 6px 12px rgba(0, 0, 0, 0.1)'
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#2c3e50',
        secondary: isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'
      },
      input: {
        background: 'transparent',
        color: isDarkMode ? '#ffffff' : '#2c3e50',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(44, 62, 80, 0.3)'
      },
      icon: {
        color: isDarkMode ? '#90cdf4' : '#3498db'
      },
      button: {
        background: isDarkMode ? '#0074cc' : '#1976d2',
        color: '#ffffff',
        hover: {
          background: isDarkMode ? '#0066b3' : '#1565c0'
        }
      },
      link: {
        color: isDarkMode ? '#90cdf4' : '#3498db',
        hover: isDarkMode ? '#63b3ed' : '#2980b9'
      },
      message: {
        color: isDarkMode ? '#4ade80' : '#38a169'
      }
    };
  }, [isDarkMode]);

  return (
    <div className="login-page-container" style={themeStyles.container}>
      <div className="login-container-main" style={themeStyles.content}>
        <div className="login-header">
          <FontAwesomeIcon icon={faEnvelope} className="header-icon" style={{ color: themeStyles.text.primary }} />
          <h2 style={{ color: themeStyles.text.primary }}>Reset Password</h2>
        </div>
        
        {!message ? (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <FontAwesomeIcon 
                icon={faEnvelope} 
                className="input-icon" 
                style={{ color: themeStyles.text.primary }} 
              />
              <input
                type="email"
                id="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                style={{
                  background: themeStyles.input.background,
                  color: themeStyles.text.primary,
                  borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(44, 62, 80, 0.3)'}`
                }}
              />
            </div>

            {error && (
              <div className="error-message" style={{ color: '#e53e3e', marginBottom: '15px', textAlign: 'center' }}>
                {error}
              </div>
            )}

            <button 
              type="submit" 
              className="password-button"
              disabled={isLoading}
              style={{
                background: isLoading ? '#ccc' : themeStyles.button.background,
                color: themeStyles.button.color,
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                  Sending...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        ) : (
          <div className="reset-success-message" style={{ textAlign: 'center', padding: '20px' }}>
            <p style={{ color: themeStyles.text.primary, marginBottom: '20px', fontWeight: '500' }}>
              {message}
            </p>
            <p style={{ color: themeStyles.text.secondary, fontSize: '14px', lineHeight: '1.5' }}>
              Please check your email and follow the instructions to reset your password.
            </p>
          </div>
        )}

        <div className="login-link">
          <Link to="/login" style={{ color: themeStyles.link.color }}>Back to Login</Link>
        </div>
      </div>
    </div>
  );
}
export default ResetPassword;
